import { useCallback, useEffect, useMemo, useState } from "react";

export type CachedPdfEntry = {
  resourceId: string;
  title?: string;
  libraryName?: string;
  libraryUsername?: string;
  targetUniversity?: string | null;
  targetFaculty?: string | null;
  targetYear?: number | null;
  sourceKey?: string | null;
  cachedAt: string;
  sizeBytes: number | null;
};

const CACHE_NAME = "athr-offline-pdfs-v1";
const STORAGE_KEY = "athr.offline-pdfs";
const STORAGE_UPDATED_EVENT = "athr-offline-pdfs-updated";
const OFFLINE_THUMB_PREFIX = "offline-thumb:";

const readEntries = (): Record<string, CachedPdfEntry> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CachedPdfEntry>) : {};
  } catch {
    return {};
  }
};

const writeEntries = (entries: Record<string, CachedPdfEntry>) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage write failures.
  }
};

const emitEntriesChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
};

const getCacheRequest = (resourceId: string) => new Request(`${window.location.origin}/__offline_pdf__/${resourceId}`);
const getThumbnailCacheRequest = (resourceId: string) => new Request(`${window.location.origin}/__offline_thumb__/${resourceId}`);

const loadCachedResponseBlobUrl = async (request: Request) => {
  if (typeof window === "undefined" || !("caches" in window)) return null;
  const cache = await window.caches.open(CACHE_NAME);
  const response = await cache.match(request);
  if (!response) return null;
  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

export const getOfflinePdfEntry = (resourceId: string) => readEntries()[resourceId] ?? null;
export const getOfflineThumbnailToken = (resourceId: string) => `${OFFLINE_THUMB_PREFIX}${resourceId}`;
export const isOfflineThumbnailToken = (value: string | null | undefined) =>
  Boolean(value && value.startsWith(OFFLINE_THUMB_PREFIX));
export const loadOfflineThumbnailBlobUrl = async (resourceId: string) =>
  loadCachedResponseBlobUrl(getThumbnailCacheRequest(resourceId));

export const listOfflinePdfEntries = () =>
  Object.values(readEntries()).sort(
    (left, right) => new Date(right.cachedAt).getTime() - new Date(left.cachedAt).getTime(),
  );

export function useOfflinePdfCache({
  resourceId,
  title,
  sourceUrl,
  sourceKey,
  resolveSourceUrl,
  thumbnailUrl,
  libraryName,
  libraryUsername,
  targetUniversity,
  targetFaculty,
  targetYear,
}: {
  resourceId?: string;
  title?: string;
  sourceUrl?: string | null;
  sourceKey?: string | null;
  resolveSourceUrl?: (() => Promise<string | null>) | null;
  thumbnailUrl?: string | null;
  libraryName?: string;
  libraryUsername?: string;
  targetUniversity?: string | null;
  targetFaculty?: string | null;
  targetYear?: number | null;
}) {
  const [cachedEntry, setCachedEntry] = useState<CachedPdfEntry | null>(null);
  const [cachedBlobUrl, setCachedBlobUrl] = useState<string | null>(null);
  const [cachedThumbnailUrl, setCachedThumbnailUrl] = useState<string | null>(null);
  const [isCaching, setIsCaching] = useState(false);

  const syncCachedEntry = useCallback(() => {
    if (!resourceId) {
      setCachedEntry(null);
      return;
    }
    setCachedEntry(readEntries()[resourceId] ?? null);
  }, [resourceId]);

  useEffect(() => {
    syncCachedEntry();
  }, [syncCachedEntry]);

  useEffect(() => {
    const handleStorageChange = () => {
      syncCachedEntry();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(STORAGE_UPDATED_EVENT, handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(STORAGE_UPDATED_EVENT, handleStorageChange);
    };
  }, [syncCachedEntry]);

  useEffect(() => {
    return () => {
      if (cachedBlobUrl) {
        window.URL.revokeObjectURL(cachedBlobUrl);
      }
      if (cachedThumbnailUrl) {
        window.URL.revokeObjectURL(cachedThumbnailUrl);
      }
    };
  }, [cachedBlobUrl, cachedThumbnailUrl]);

  const loadCachedBlobUrl = useCallback(async () => {
    if (!resourceId) return null;
    const blobUrl = await loadCachedResponseBlobUrl(getCacheRequest(resourceId));
    if (!blobUrl) return null;
    setCachedBlobUrl((current) => {
      if (current) {
        window.URL.revokeObjectURL(current);
      }
      return blobUrl;
    });
    return blobUrl;
  }, [resourceId]);

  useEffect(() => {
    if (!resourceId || !cachedEntry) return;
    void loadCachedBlobUrl();
  }, [cachedEntry, loadCachedBlobUrl, resourceId]);

  const loadCachedThumbnail = useCallback(async () => {
    if (!resourceId) return null;
    const blobUrl = await loadCachedResponseBlobUrl(getThumbnailCacheRequest(resourceId));
    if (!blobUrl) return null;
    setCachedThumbnailUrl((current) => {
      if (current) {
        window.URL.revokeObjectURL(current);
      }
      return blobUrl;
    });
    return blobUrl;
  }, [resourceId]);

  useEffect(() => {
    if (!resourceId || !cachedEntry) return;
    void loadCachedThumbnail();
  }, [cachedEntry, loadCachedThumbnail, resourceId]);

  const cachePdf = useCallback(async () => {
    if (!resourceId || typeof window === "undefined" || !("caches" in window)) {
      throw new Error("Caching is not available");
    }

    setIsCaching(true);
    try {
      const candidateUrls = new Set<string>();
      if (sourceUrl) {
        candidateUrls.add(sourceUrl);
      }

      if (resolveSourceUrl) {
        const refreshedSourceUrl = await resolveSourceUrl();
        if (refreshedSourceUrl) {
          candidateUrls.add(refreshedSourceUrl);
        }
      }

      let response: Response | null = null;
      let lastError: Error | null = null;

      for (const candidateUrl of candidateUrls) {
        try {
          const candidateResponse = await fetch(candidateUrl, { cache: "no-store" });
          if (!candidateResponse.ok) {
            throw new Error(`Failed to fetch PDF for offline use (${candidateResponse.status})`);
          }
          response = candidateResponse;
          break;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error("Failed to fetch PDF for offline use");
        }
      }

      if (!response) {
        // Keep any existing offline copy if refetching fails (e.g. source was deleted and later re-uploaded).
        const existingBlobUrl = await loadCachedBlobUrl();
        if (existingBlobUrl) {
          return;
        }
        throw lastError ?? new Error("Failed to fetch PDF for offline use");
      }

      const clone = response.clone();
      const buffer = await clone.arrayBuffer();
      const cache = await window.caches.open(CACHE_NAME);
      await cache.put(getCacheRequest(resourceId), response);
      if (thumbnailUrl) {
        try {
          const thumbnailResponse = await fetch(thumbnailUrl);
          if (thumbnailResponse.ok) {
            await cache.put(getThumbnailCacheRequest(resourceId), thumbnailResponse);
          }
        } catch {
          // Ignore thumbnail caching failures.
        }
      }

      const entries = readEntries();
      const nextEntry: CachedPdfEntry = {
        resourceId,
        title,
        libraryName,
        libraryUsername,
        targetUniversity,
        targetFaculty,
        targetYear,
        sourceKey: sourceKey ?? null,
        cachedAt: new Date().toISOString(),
        sizeBytes: buffer.byteLength,
      };
      entries[resourceId] = nextEntry;
      writeEntries(entries);
      setCachedEntry(nextEntry);
      emitEntriesChanged();
      await loadCachedBlobUrl();
      await loadCachedThumbnail();
    } finally {
      setIsCaching(false);
    }
  }, [libraryName, libraryUsername, loadCachedBlobUrl, loadCachedThumbnail, resolveSourceUrl, resourceId, sourceKey, sourceUrl, targetFaculty, targetUniversity, targetYear, thumbnailUrl, title]);

  const removeCachedPdf = useCallback(async () => {
    if (!resourceId || typeof window === "undefined" || !("caches" in window)) return;
    const cache = await window.caches.open(CACHE_NAME);
    await cache.delete(getCacheRequest(resourceId));
    await cache.delete(getThumbnailCacheRequest(resourceId));
    const entries = readEntries();
    delete entries[resourceId];
    writeEntries(entries);
    setCachedEntry(null);
    emitEntriesChanged();
    setCachedBlobUrl((current) => {
      if (current) {
        window.URL.revokeObjectURL(current);
      }
      return null;
    });
    setCachedThumbnailUrl((current) => {
      if (current) {
        window.URL.revokeObjectURL(current);
      }
      return null;
    });
  }, [resourceId]);

  const resolvedUrl = useMemo(() => cachedBlobUrl ?? sourceUrl, [cachedBlobUrl, sourceUrl]);

  return {
    cachedEntry,
    isCaching,
    isCached: Boolean(cachedEntry),
    resolvedUrl,
    cachePdf,
    removeCachedPdf,
    loadCachedBlobUrl,
    cachedThumbnailUrl,
  };
}

export function useOfflinePdfLibrary() {
  const [entries, setEntries] = useState<CachedPdfEntry[]>(() => listOfflinePdfEntries());

  useEffect(() => {
    const syncEntries = () => {
      setEntries(listOfflinePdfEntries());
    };

    window.addEventListener("storage", syncEntries);
    window.addEventListener(STORAGE_UPDATED_EVENT, syncEntries);
    return () => {
      window.removeEventListener("storage", syncEntries);
      window.removeEventListener(STORAGE_UPDATED_EVENT, syncEntries);
    };
  }, []);

  const removeEntry = useCallback(async (resourceId: string) => {
    if (typeof window === "undefined" || !("caches" in window)) return;
    const cache = await window.caches.open(CACHE_NAME);
    await cache.delete(getCacheRequest(resourceId));
    await cache.delete(getThumbnailCacheRequest(resourceId));
    const entriesById = readEntries();
    delete entriesById[resourceId];
    writeEntries(entriesById);
    emitEntriesChanged();
    setEntries(listOfflinePdfEntries());
  }, []);

  return {
    entries,
    removeEntry,
  };
}
